import React from 'react'

import { Button, ButtonColorVariant, Select } from '@gousto-internal/citrus-react'

import { BottomFixedContentWrapper } from '../components/BottomFixedContentWrapper'
import { GetHelpLayout } from '../layouts/GetHelpLayout'

import css from './IngredientIssues.css'

interface ContentProps {
  title: string
  body: string
  button1Copy: string
}

interface IngredientInfo {
  recipeId: string
  ingredientUuid: string
  issueId?: string
  issueName?: string
  label: string
}

interface IssueType {
  id: string
  label: string
  requireDescription: boolean
}

interface SubIssueType {
  id: string
  label: string
  groupLabel: string
  requireDescription: boolean
}

interface IngredientIssuesPresentationProps {
  content: ContentProps
  changeHandler: (ingredientAndRecipeId: string, value: string) => void
  continueHandler: () => void
  ingredients?: Record<string, IngredientInfo>
  issues?: IssueType[]
  subIssues?: SubIssueType[]
}

const renderIngredientsIssues = (
  ingredients: Record<string, IngredientInfo>,
  issues: IssueType[],
  subIssues: SubIssueType[],
  changeHandler: (ingredientAndRecipeId: string, value: string) => void,
) => {
  const groupedSubIssues = subIssues.reduce((groups: Record<string, SubIssueType[]>, subIssue) => {
    const { groupLabel } = subIssue
    const updatedGroups = { ...groups }
    if (!updatedGroups[groupLabel]) {
      updatedGroups[groupLabel] = []
    }
    updatedGroups[groupLabel].push(subIssue)

    return updatedGroups
  }, {})

  const renderedIngredients = Object.keys(ingredients).map((ingredientAndRecipeId) => {
    const optionSelected = issues?.[0]?.id
    const ingredientLabel = ingredients[ingredientAndRecipeId].label

    return (
      <div key={ingredientAndRecipeId}>
        <p className={css.ingredientLabel}>{ingredientLabel}</p>
        {optionSelected && (
          <Select
            id={ingredientAndRecipeId}
            defaultValue={optionSelected}
            onChange={(e) =>
              changeHandler(ingredientAndRecipeId, (e.target as HTMLSelectElement).value)
            }
          >
            {issues.map((issue) => (
              <option key={issue.id} value={issue.id}>
                {issue.label}
              </option>
            ))}
            {Object.keys(groupedSubIssues).map((groupLabel) => (
              <optgroup key={groupLabel} label={groupLabel}>
                {groupedSubIssues[groupLabel].map((subIssue) => (
                  <option key={subIssue.id} value={subIssue.id}>
                    {subIssue.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        )}
      </div>
    )
  })

  return renderedIngredients
}

const IngredientIssuesPresentation: React.FC<IngredientIssuesPresentationProps> = ({
  content: { title, body, button1Copy },
  ingredients = {},
  issues = [],
  subIssues = [],
  changeHandler,
  continueHandler,
}) => (
  <GetHelpLayout title={title} body={body}>
    {renderIngredientsIssues(ingredients, issues, subIssues, changeHandler)}
    <BottomFixedContentWrapper>
      <Button
        className={css.button}
        colorVariant={ButtonColorVariant.Primary}
        onClick={continueHandler}
      >
        {button1Copy}
      </Button>
    </BottomFixedContentWrapper>
  </GetHelpLayout>
)

export { IngredientIssuesPresentation }
